import React from 'react';
import { Sparkles, Clock, Bell, Target, ArrowRight, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useScrollAnimation, useStaggerAnimation } from '../hooks/useScrollAnimation';

const SolutionSection = () => {
  const { t, isRTL } = useLanguage();
  const [headerRef, headerVisible] = useScrollAnimation();
  const [cardsRef, visibleCards] = useStaggerAnimation(4, 150);

  const features = [
    { icon: Sparkles, color: '#0071E3', ...t.solution.features[0] },
    { icon: Clock, color: '#34C759', ...t.solution.features[1] },
    { icon: Bell, color: '#FF9500', ...t.solution.features[2] },
    { icon: Target, color: '#5856D6', ...t.solution.features[3] }
  ];

  return (
    <section id="how-it-works" className="py-20 md:py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-14 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-[#34C759]/10 rounded-full mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-1.5 h-1.5 bg-[#34C759] rounded-full animate-pulse"></div>
            <span className="text-[11px] font-semibold text-[#34C759] uppercase tracking-wide font-inter">
              {t.solution.badge}
            </span>
          </div>
          <h2 className="font-dm text-3xl md:text-4xl lg:text-[42px] font-semibold tracking-tight text-[#1D1D1F] mb-4 leading-tight">
            {t.solution.title}
          </h2>
          <p className="text-lg text-[#6e6e73] font-inter max-w-2xl mx-auto">
            {t.solution.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className={`group relative bg-[#F5F5F7] rounded-2xl p-6 hover:bg-white hover:shadow-xl border border-transparent hover:border-[#E5E5E5] transition-all duration-500 hover-lift ${
                visibleCards.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${isRTL ? 'text-right' : ''}`}
            >
              {/* Icon */}
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300"
                style={{ backgroundColor: `${feature.color}15` }}
              >
                <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
              </div>
              
              {/* Content */}
              <h3 className="font-dm text-lg font-semibold text-[#1D1D1F] mb-2 group-hover:text-[#0071E3] transition-colors">
                {feature.title}
              </h3>
              <p className="text-[#6e6e73] font-inter text-[15px] leading-relaxed">
                {feature.desc}
              </p>
              
              {/* Hover Arrow */}
              <div className={`absolute bottom-6 ${isRTL ? 'left-6' : 'right-6'} opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-1`}>
                <ArrowRight className={`w-5 h-5 text-[#0071E3] ${isRTL ? 'rotate-180' : ''}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div 
          className={`mt-12 text-center transition-all duration-700 delay-500 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className={`inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-[#0071E3]/10 to-[#5856D6]/10 rounded-full hover:from-[#0071E3]/20 hover:to-[#5856D6]/20 transition-all duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <CheckCircle className="w-5 h-5 text-[#34C759]" />
            <span className="text-[#1D1D1F] font-inter font-medium text-[15px]">
              All running automatically in the background
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
